/**
 * Read-only rich markdown renderer for the Design tab — renders markdown
 * (including GFM tables, task lists, code blocks) via the same TipTap engine
 * the section editor uses, so previews look exactly like the editable surface.
 * Used for wizard / Hammer draft previews where Foundry's lightweight
 * MarkdownContent can't render tables.
 */
import { useEffect } from 'react';
import type { ReactElement } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { buildRichExtensions } from './lib/editor-extensions';
import { MarkdownContent } from '../../components/MarkdownContent';
import { TrustedHtmlFallbackBoundary } from './lib/TrustedHtmlFallbackBoundary';
import { shouldUseTrustedHtmlFallbackForSession } from './lib/trusted-types';

export interface RichMarkdownProps {
  content: string;
  className?: string;
}

export function RichMarkdown({ content, className }: RichMarkdownProps): ReactElement {
  const rootClassName = `design-wysiwyg ${className ?? ''}`.trim();
  const fallback = <MarkdownContent content={content} className={rootClassName} />;
  if (shouldUseTrustedHtmlFallbackForSession()) return fallback;

  return (
    <TrustedHtmlFallbackBoundary fallback={fallback}>
      <TiptapRichMarkdown content={content} className={rootClassName} />
    </TrustedHtmlFallbackBoundary>
  );
}

function TiptapRichMarkdown({
  content,
  className,
}: {
  content: string;
  className: string;
}): ReactElement {
  const editor = useEditor({
    extensions: buildRichExtensions(),
    content,
    editable: false,
    editorProps: { attributes: { class: className } },
  });

  // Re-render when the source markdown changes (streaming draft, switched item).
  useEffect(() => {
    if (!editor) return;
    const current = editor.storage.markdown.getMarkdown() as string;
    if (content !== current) editor.commands.setContent(content, false);
  }, [content, editor]);

  return <EditorContent editor={editor} />;
}
